import styled from "@emotion/styled/macro";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Banner,
  BannerTypes,
  Button,
  SvgIcon,
  SvgNames,
  Tooltip,
  tokens,
  Typography,
} from "@sunrun/experience-ui-components";
import { type ContractReviewMessage } from "../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import { NON_BLOCKING_COPY } from "constants/contractReviewNonBlockingCopy";
import {
  getContractReviewMessageDismissKey,
  getMessagesByCategory,
  type ContractReviewCategory,
} from "utils/contractReviewUtils";
import { Box } from "components/atoms/Box";

const DISMISS_STORAGE_PREFIX = "offerexp.contractReviewBanner.dismissed.v1.";

const dismissStorageKey = (sessionDismissScopeId: string) =>
  `${DISMISS_STORAGE_PREFIX}${sessionDismissScopeId}`;

const readDismissedFingerprints = (
  sessionDismissScopeId: string | undefined
): Set<string> => {
  if (!sessionDismissScopeId || typeof sessionStorage === "undefined") {
    return new Set();
  }
  try {
    const raw = sessionStorage.getItem(
      dismissStorageKey(sessionDismissScopeId)
    );
    if (!raw) {
      return new Set();
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return new Set();
    }
    return new Set(parsed.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
};

const persistDismissedFingerprints = (
  sessionDismissScopeId: string,
  fingerprints: Set<string>
) => {
  sessionStorage.setItem(
    dismissStorageKey(sessionDismissScopeId),
    JSON.stringify([...fingerprints])
  );
};

type ContractReviewBannersProps = {
  messages: ContractReviewMessage[];
  category: ContractReviewCategory;
  filter?: (message: ContractReviewMessage) => boolean;
  actionLink?: { text: string; onClick: () => void };
  /**
   * persists warning banners in `sessionStorage` for that tab.
   */
  sessionDismissScopeId?: string;
  bannerTypeOverride?: BannerTypes;
};

const getBannerType = (type: ContractReviewMessage["type"]): BannerTypes =>
  type === "info" ? BannerTypes.InfoLight : BannerTypes.WarningLight;

const getBannerAccentColor = (bannerType: BannerTypes): string => {
  switch (bannerType) {
    case BannerTypes.ErrorLight:
    case BannerTypes.ErrorDark:
      return tokens.ERROR_20;
    case BannerTypes.InfoLight:
    case BannerTypes.InfoDark:
      return tokens.INFO_20;
    default:
      return tokens.WARNING_20;
  }
};

const ContractReviewBanners = ({
  messages,
  category,
  filter,
  actionLink,
  sessionDismissScopeId,
  bannerTypeOverride,
}: ContractReviewBannersProps) => {
  const categoryFilter = getMessagesByCategory(messages, category);
  const afterPropFilter = filter
    ? categoryFilter.filter(filter)
    : categoryFilter;

  const [dismissedFingerprints, setDismissedFingerprints] = useState<
    Set<string>
  >(() => readDismissedFingerprints(sessionDismissScopeId));

  useEffect(() => {
    setDismissedFingerprints(readDismissedFingerprints(sessionDismissScopeId));
  }, [sessionDismissScopeId]);

  const handleDismissWarning = useCallback(
    (msg: ContractReviewMessage) => {
      if (!sessionDismissScopeId) {
        return;
      }
      const fp = getContractReviewMessageDismissKey(msg);
      setDismissedFingerprints((prev) => {
        const next = new Set(prev);
        next.add(fp);
        persistDismissedFingerprints(sessionDismissScopeId, next);
        return next;
      });
    },
    [sessionDismissScopeId]
  );

  const visibleMessages = useMemo(() => {
    if (!sessionDismissScopeId) {
      return afterPropFilter;
    }
    return afterPropFilter.filter((m) => {
      if (m.type === "info") {
        return true;
      }
      return !dismissedFingerprints.has(getContractReviewMessageDismissKey(m));
    });
  }, [afterPropFilter, dismissedFingerprints, sessionDismissScopeId]);

  if (visibleMessages.length === 0) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gap="16px" marginBottom="16px">
      {visibleMessages.map((msg, index) => (
        <ContractReviewBannerRow
          key={`${getContractReviewMessageDismissKey(msg)}-${index}`}
          msg={msg}
          actionLink={actionLink}
          sessionDismissScopeId={sessionDismissScopeId}
          onDismissWarning={handleDismissWarning}
          bannerTypeOverride={bannerTypeOverride}
        />
      ))}
    </Box>
  );
};

type ContractReviewBannerRowProps = {
  msg: ContractReviewMessage;
  actionLink?: { text: string; onClick: () => void };
  sessionDismissScopeId?: string;
  onDismissWarning: (msg: ContractReviewMessage) => void;
  bannerTypeOverride?: BannerTypes;
};

const ContractReviewBannerRow = ({
  msg,
  actionLink,
  sessionDismissScopeId,
  onDismissWarning,
  bannerTypeOverride,
}: ContractReviewBannerRowProps) => {
  const useTitleRowWithTooltip = msg.type === "warning" || msg.type === "error";
  const effectiveBannerType = bannerTypeOverride ?? getBannerType(msg.type);
  const accentColor = getBannerAccentColor(effectiveBannerType);

  const customButtons = useMemo(() => {
    const showActionLink = actionLink && msg.type !== "info";
    const showDismiss =
      ["warning", "error"].includes(msg.type) && Boolean(sessionDismissScopeId);

    if (!showActionLink && !showDismiss) {
      return undefined;
    }

    return (
      <CustomButtonsGroup>
        {showDismiss ? (
          <DismissLinkButton
            color="link"
            size="sm"
            accentColor={accentColor}
            data-testid="contract-review-banner-dismiss"
            onClick={() => onDismissWarning(msg)}
          >
            Dismiss
          </DismissLinkButton>
        ) : null}
        {showActionLink ? (
          <ActionButton
            color="link"
            size="sm"
            accentColor={accentColor}
            onClick={actionLink!.onClick}
          >
            {actionLink!.text}
          </ActionButton>
        ) : null}
      </CustomButtonsGroup>
    );
  }, [accentColor, actionLink, msg, onDismissWarning, sessionDismissScopeId]);

  if (useTitleRowWithTooltip) {
    return (
      <Banner
        bannerType={effectiveBannerType}
        show
        showClose={false}
        customButtons={customButtons}
      >
        <WarningBodyStack>
          <WarningTitleRow>
            <WarningTitleText variant="span" color={accentColor}>
              {msg.message}
            </WarningTitleText>
            <NonBlockingTooltip
              className="icon-tooltip"
              content={NON_BLOCKING_COPY}
              direction="top"
            >
              <InfoTooltipTrigger
                type="button"
                accentColor={accentColor}
                aria-label="More about non-blocking contract review warnings"
              >
                <SvgIcon
                  name={SvgNames.InfoOutline}
                  width={20}
                  height={20}
                  color={accentColor}
                />
              </InfoTooltipTrigger>
            </NonBlockingTooltip>
          </WarningTitleRow>
          {msg.suggestion ? (
            <WarningBannerSubtitle color="text.secondary">
              {msg.suggestion}
            </WarningBannerSubtitle>
          ) : null}
        </WarningBodyStack>
      </Banner>
    );
  }

  return (
    <Banner
      bannerType={effectiveBannerType}
      title={msg.message}
      show
      showClose={false}
      customButtons={customButtons}
    >
      {msg.suggestion && (
        <Typography color="text.secondary">{msg.suggestion}</Typography>
      )}
    </Banner>
  );
};

const CustomButtonsGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;

const WarningBodyStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const WarningTitleRow = styled.div`
  display: block;
  width: 100%;
`;

const WarningTitleText = styled(Typography)`
  display: inline;
  font-weight: 500;
  margin: 0;
  padding: 0;
`;

const WarningBannerSubtitle = styled(Typography)`
  font-size: 12px;
  line-height: 1.35;
`;

const NonBlockingTooltip = styled(Tooltip)`
  display: inline-flex;
  vertical-align: text-bottom;
  margin-left: 0.25em;

  .sr-tooltip-children {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    max-width: 100%;
  }

  [role="tooltip"] {
    width: 400px;
    max-width: 400px;
    box-sizing: border-box;
  }
`;

const InfoTooltipTrigger = styled.button<{ accentColor: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  line-height: 1;
  vertical-align: inherit;
  color: inherit;

  &:focus-visible {
    outline: 2px solid ${({ accentColor }) => accentColor};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const ActionButton = styled(Button)<{ accentColor: string }>`
  &&.sr-button-link {
    color: ${({ accentColor }) => accentColor};
    border-bottom-color: ${({ accentColor }) => accentColor};
  }
`;

const DismissLinkButton = styled(Button)<{ accentColor: string }>`
  &&.sr-button-link {
    color: ${({ accentColor }) => accentColor};
    border-bottom-color: ${({ accentColor }) => accentColor};
  }
`;

export { ContractReviewBanners };
export type { ContractReviewBannersProps };
